import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData, updateData } from '../../../apiService';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button } from '@material-ui/core';
import Select from "react-select";
import CircularProgress from '@mui/material/CircularProgress';
import {
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';

const PurchaseOrderEdit = () => {
    const { id } = useParams();
    const [selectedItems, setSelectedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { isAuthenticated, companyId, branchId, userId, token, logout, branchstate } = useAuth();
    const [formData, setFormData] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const history = useHistory();
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedInvoiceType, setSelectedInvoiceType] = useState(null);
    const [dropdownInvoiceTypeOptions, setDropdownInvoiceTypeOptions] = useState([]);
    const [itemToDeleteIndex, setItemToDeleteIndex] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        fetchInvoicetypeData();
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        fetchData('PurchaseOrderApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    const order = response.response_data.PurchaseOrder;
                    setFormData(order);
                    setSelectedItems(response.response_data.PurchaseOrderDetails);
                    setSelectedOption({ value: order.MasterId, label: order.SupplierName });
                    setSelectedInvoiceType({ value: order.InvoiceTypeId, label: order.InvoiceTypeName });
                }
            })
            .catch((error) => console.error('Error fetching Purchase Order:', error));
    }, [id]);

    const fetchInvoicetypeData = () => {
        fetchData('PurchaseApi', `/InvoiceType/${companyId}/${branchId}`, token)
            .then((response) => {
                if (response?.response_data && Array.isArray(response.response_data)) {
                    setDropdownInvoiceTypeOptions(response.response_data);
                }
            })
            .catch((error) => console.error('Error fetching invoice type:', error));
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const fetchOptions = async (inputValue) => {
        if (inputValue.length <= 2) return [];
        try {
            const response = await fetchData('PurchaseApi', `/Party/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111') {
                const data = response.response_data;
                if (Array.isArray(data)) {
                    setSupplierOptions(data);
                    return data.map(item => ({ value: item.Id, label: item.Name }));
                }
            }
        } catch (error) { console.error('Error fetching options:', error); }
        return [];
    };

    const handleCustomerSelect = (selected) => {
        setSelectedOption(selected);
        setFormData(prev => ({ ...prev, MasterId: selected ? selected.value : '' }));
        if (!selected) return;
        const supplier = supplierOptions.find(s => s.Id === selected.value);
        const gst = supplier?.GSTNumber ?? '';
        let invoiceTypeId = 2;
        if (gst) invoiceTypeId = gst.substring(0, 2) !== branchstate ? 3 : 1;
        setSelectedInvoiceType({ value: invoiceTypeId, label: invoiceTypeId === 3 ? 'IGST' : invoiceTypeId === 1 ? 'B To B' : 'B To C' });
        setFormData(prev => ({ ...prev, InvoiceTypeId: invoiceTypeId, GSTNumber: gst }));
    };

    const parseAndFix = (val) => parseFloat(val) || 0;

    const calculateItemData = (item) => {
        const rate = parseAndFix(item.Rate);
        const qty = parseAndFix(item.Quantity);
        const amt = (qty * rate).toFixed(2);
        const discAmt = parseAndFix(item.DiscountAmount) > 0
            ? parseAndFix(item.DiscountAmount)
            : (parseAndFix(amt) * parseAndFix(item.DiscountPercentage)) / 100;
        const taxable = (parseAndFix(amt) - discAmt).toFixed(2);
        const cgstRate = parseAndFix(item.GSTRate) / 2;
        const sgstRate = parseAndFix(item.GSTRate) / 2;
        let cgst = 0, sgst = 0, igst = 0;
        if (formData.InvoiceTypeId === 3) {
            igst = ((parseAndFix(taxable) * (cgstRate + sgstRate)) / 100).toFixed(2);
        } else {
            cgst = ((parseAndFix(taxable) * cgstRate) / 100).toFixed(2);
            sgst = ((parseAndFix(taxable) * sgstRate) / 100).toFixed(2);
        }
        const total = parseAndFix(taxable) + parseAndFix(cgst) + parseAndFix(sgst) + parseAndFix(igst);
        return { ...item, Amount: amt, DiscountAmount: discAmt.toFixed(2), TaxableAmount: taxable, CGSTAmount: cgst, SGSTAmount: sgst, IGSTAmount: igst, TotalAmount: total.toFixed(2) };
    };

    const recalculateTotals = (items) => {
        const totals = items.reduce((acc, item) => {
            acc.Quantity += parseAndFix(item.Quantity);
            acc.Amount += parseAndFix(item.Amount);
            acc.DiscountAmount += parseAndFix(item.DiscountAmount);
            acc.TaxableAmount += parseAndFix(item.TaxableAmount);
            acc.CGSTAmount += parseAndFix(item.CGSTAmount);
            acc.SGSTAmount += parseAndFix(item.SGSTAmount);
            acc.IGSTAmount += parseAndFix(item.IGSTAmount);
            acc.TotalAmount += parseAndFix(item.TotalAmount);
            return acc;
        }, { Quantity: 0, Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

        const roundOff = Math.round(totals.TotalAmount) - totals.TotalAmount;
        const grandTotal = totals.TotalAmount + roundOff;
        setFormData(prev => ({
            ...prev,
            TotalQuantity: totals.Quantity,
            Amount: totals.Amount.toFixed(2),
            TaxableAmount: totals.TaxableAmount.toFixed(2),
            CGSTAmount: totals.CGSTAmount.toFixed(2),
            SGSTAmount: totals.SGSTAmount.toFixed(2),
            IGSTAmount: totals.IGSTAmount.toFixed(2),
            DiscountAmount: totals.DiscountAmount.toFixed(2),
            RoundOff: roundOff.toFixed(2),
            TotalAmount: grandTotal.toFixed(2),
        }));
    };

    const handleCalculation = (e, index, inputType) => {
        const name = typeof e === 'object' ? e.target.name : inputType;
        const value = typeof e === 'object' ? e.target.value : e;
        setSelectedItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [name]: value };
            const recalculated = updated.map(calculateItemData);
            recalculateTotals(recalculated);
            return recalculated;
        });
    };

    const fetchItemOptions = async (inputValue = '') => {
        if (!inputValue || inputValue.length <= 1) return [];
        try {
            const response = await fetchData('PurchaseApi', `/Item/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111' && Array.isArray(response.response_data)) {
                setItemsOptions(response.response_data);
                return response.response_data.map(item => ({
                    value: item.Id,
                    label: item.ItemName1,
                    PurchaseRate: item.PurchaseRate || 0,
                    GSTRate: (parseFloat(item.CGSTRate || 0) * 2).toFixed(2),
                    HSNCode: item.HSNCode || '',
                    ItemName: item.ItemName || item.ItemName1,
                    BalanceQuantity: item.BalanceStock || 0,
                }));
            }
        } catch (error) { console.error('Error fetching items:', error); }
        return [];
    };

    // Item entry modal state
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [pendingItem, setPendingItem] = useState(null);
    const [itemModalQty, setItemModalQty] = useState(1);
    const [itemModalRate, setItemModalRate] = useState(0);
    const [itemsOptions, setItemsOptions] = useState([]);

    const handleItemSelect = (selected) => {
        if (!selected) return;
        const itemDetail = itemsOptions.find(i => i.Id === selected.value);
        const balanceQuantity = itemDetail?.BalanceStock || 0;
        setPendingItem({ ...selected, BalanceQuantity: balanceQuantity });
        setItemModalQty(1);
        setItemModalRate(selected.PurchaseRate || 0);
        setItemModalOpen(true);
    };

    const handleItemModalConfirm = () => {
        if (!pendingItem) return;
        const newItem = {
            ItemId: pendingItem.value,
            ItemName: pendingItem.label,
            Quantity: itemModalQty,
            Rate: itemModalRate,
            GSTRate: pendingItem.GSTRate || 0,
            BalanceQuantity: pendingItem.BalanceQuantity || 0,
            DiscountPercentage: 0,
            DiscountAmount: 0,
            Amount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0,
        };
        const calculated = calculateItemData(newItem);
        setSelectedItems(prev => {
            const updated = [...prev, calculated];
            recalculateTotals(updated);
            return updated;
        });
        setItemModalOpen(false);
        setPendingItem(null);
    };

    const openConfirmDialog = (index) => { setItemToDeleteIndex(index); setConfirmDialogOpen(true); };
    const closeConfirmDialog = () => { setConfirmDialogOpen(false); setItemToDeleteIndex(null); };

    const handleDelete = () => {
        if (itemToDeleteIndex !== null) {
            setSelectedItems(prev => {
                const updated = prev.filter((_, i) => i !== itemToDeleteIndex).map(calculateItemData);
                recalculateTotals(updated);
                return updated;
            });
            closeConfirmDialog();
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        if (!formData.BillNumber) { setErrorMessage('Bill Number is required!'); setIsLoading(false); return; }
        if (!formData.MasterId) { setErrorMessage('Supplier is required!'); setIsLoading(false); return; }
        const payload = {
            PurchaseOrder: { ...formData, ModifiedBy: userId },
            PurchaseOrderDetails: selectedItems
        };
        updateData('PurchaseOrderApi', id, payload, token)
            .then(() => history.push('/PurchaseOrderIndex'))
            .catch((error) => { console.error('Error updating record:', error); setIsLoading(false); });
    };

    const filteredData = selectedItems.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div style={{ fontFamily: "Segoe UI, sans-serif", fontSize: "12px", width: "100%", padding: "5px", marginTop: '-10px' }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #ccc", paddingBottom: "6px", marginBottom: "12px" }}>
                <h6 className="uppercase tracking-wide font-serif text-black mt-2">Purchase Order - Edit</h6>
                <div className="flex items-center gap-3 mt-2">
                    <Button variant="contained" component={Link} to="/PurchaseOrderIndex"
                        onClick={() => localStorage.setItem("shouldReload", "true")}
                        style={{ height: "25px", minWidth: "90px", borderRadius: "6px", backgroundColor: "#F3F4F6", color: "#1E3A8A", fontWeight: 600, fontSize: "13px", textTransform: "none" }}>
                        Back To List
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={isLoading}
                        style={{ height: "25px", minWidth: "90px", borderRadius: "6px", backgroundColor: "#3F4F44", color: "white", fontWeight: 600, fontSize: "13px", textTransform: "none" }}>
                        {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Update'}
                    </Button>
                </div>
            </div>

            {errorMessage && <p style={{ color: 'red', fontSize: '12px' }}>{errorMessage}</p>}

            {/* Top Form */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px", marginBottom: "12px" }}>
                <div style={{ gridColumn: "span 2" }}>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Supplier</label>
                    <AsyncSelect
                        cacheOptions loadOptions={fetchOptions} defaultOptions
                        onChange={handleCustomerSelect} value={selectedOption}
                        placeholder="Search supplier..." isClearable
                        styles={{ control: (b) => ({ ...b, minHeight: '28px', fontSize: '12px' }) }}
                    />
                </div>
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Invoice Type</label>
                    <Select
                        options={dropdownInvoiceTypeOptions.map(o => ({ value: o.Id, label: o.Name }))}
                        value={selectedInvoiceType}
                        onChange={(sel) => { setSelectedInvoiceType(sel); setFormData(prev => ({ ...prev, InvoiceTypeId: sel?.value })); }}
                        styles={{ control: (b) => ({ ...b, minHeight: '28px', fontSize: '12px' }) }}
                    />
                </div>
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Bill Date</label>
                    <input type="date" name="BillDate" value={formatDateForInput(formData.BillDate)} onChange={handleChange}
                        style={{ border: "1px solid #ccc", borderRadius: "4px", fontSize: "12px", height: "28px", width: "100%", padding: "0 6px" }} />
                </div>
                <div>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Bill Number</label>
                    <input type="text" name="BillNumber" value={formData.BillNumber || ''} onChange={handleChange}
                        style={{ border: "1px solid #ccc", borderRadius: "4px", fontSize: "12px", height: "28px", width: "100%", padding: "0 6px" }} />
                </div>

            </div>

            {/* Item Search */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "8px", alignItems: "center" }}>
                <div style={{ width: "300px" }}>
                    <label style={{ fontSize: "11px", fontWeight: "600" }}>Add Item</label>
                    <AsyncSelect
                        cacheOptions loadOptions={fetchItemOptions} defaultOptions
                        onChange={handleItemSelect} value={null}
                        placeholder="Search item..." isClearable
                        styles={{ control: (b) => ({ ...b, minHeight: '28px', fontSize: '12px' }) }}
                    />
                </div>
                <div style={{ marginTop: '16px' }}>
                    <input type="text" placeholder="🔍 Search items..." value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                        style={{ width: isFocused ? "200px" : "160px", height: "28px", border: "1px solid #ddd", borderRadius: "20px", padding: "0 12px", fontSize: "12px", transition: "all 0.3s ease", outline: "none" }} />
                </div>
            </div>

            {/* Items Table */}
            <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #999", borderRadius: "4px", marginBottom: "12px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead style={{ backgroundColor: "#1F2A38", color: "#FFFFFF", position: "sticky", top: 0 }}>
                        <tr>
                            {["#", "Item", "Qty", "Rate", "Amount", "Disc%", "Disc₹", "Taxable", "GST%", "Bal.Qty", "Total", ""].map((h, i) => (
                                <th key={i} style={{ padding: "4px 6px", textAlign: i > 1 ? "right" : "left", fontSize: "10px", textTransform: "uppercase" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((item, index) => (
                            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#FDFDFD" : "#F5F7FA", borderBottom: "1px solid #ddd" }}>
                                <td style={{ padding: "3px 6px", textAlign: "center" }}>{index + 1}</td>
                                <td style={{ padding: "3px", minWidth: "160px" }}>{item.ItemName}</td>
                                <td style={{ padding: "2px" }}>
                                    <input type="number" value={item.Quantity} name="Quantity" onChange={(e) => handleCalculation(e, index)}
                                        style={{ width: "70px", textAlign: "right", border: "1px solid #bbb", borderRadius: "3px", padding: "2px" }} />
                                </td>
                                <td style={{ padding: "2px" }}>
                                    <input type="number" value={item.Rate} name="Rate" onChange={(e) => handleCalculation(e, index)}
                                        style={{ width: "100px", textAlign: "right", border: "1px solid #bbb", borderRadius: "3px", padding: "2px" }} />
                                </td>
                                <td style={{ padding: "2px" }}>
                                    <input value={item.Amount || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #ccc", borderRadius: "3px", padding: "2px" }} />
                                </td>
                                <td style={{ padding: "2px" }}>
                                    <input type="number" value={item.DiscountPercentage || 0} name="DiscountPercentage" onChange={(e) => handleCalculation(e, index)}
                                        style={{ width: "70px", textAlign: "right", border: "1px solid #bbb", borderRadius: "3px", padding: "2px" }} />
                                </td>
                                <td style={{ padding: "2px" }}>
                                    <input type="number" value={item.DiscountAmount || 0} name="DiscountAmount" onChange={(e) => handleCalculation(e, index)}
                                        style={{ width: "100px", textAlign: "right", border: "1px solid #bbb", borderRadius: "3px", padding: "2px" }} />
                                </td>
                                <td style={{ padding: "2px" }}>
                                    <input value={item.TaxableAmount || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #ccc", borderRadius: "3px", padding: "2px" }} />
                                </td>
                                <td style={{ padding: "2px", textAlign: "right" }}>
                                    <span style={{ color: "darkmagenta", fontSize: "11px" }}>{item.GSTRate || 0}%</span>
                                </td>
                                <td style={{ padding: "2px", textAlign: "right", width: "70px" }}>
                                    <span style={{ color: "#2e7d32", fontWeight: "600", fontSize: "11px" }}>{item.BalanceQuantity || 0}</span>
                                </td>
                                <td style={{ padding: "2px" }}>
                                    <input value={item.TotalAmount || 0} readOnly style={{ width: "100px", textAlign: "right", border: "1px solid #888", borderRadius: "3px", padding: "2px", fontWeight: "bold" }} />
                                </td>
                                <td style={{ padding: "2px", textAlign: "center" }}>
                                    <DeleteIcon onClick={() => openConfirmDialog(index)} style={{ fontSize: '18px', color: '#e53935', cursor: 'pointer' }} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Summary */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "55px", fontSize: "12px", alignItems: "center" }}>
                {[['Amount', formData.Amount], ['Discount', formData.DiscountAmount], ['Taxable', formData.TaxableAmount], ['CGST', formData.CGSTAmount], ['SGST', formData.SGSTAmount], ['IGST', formData.IGSTAmount], ['Round Off', formData.RoundOff]].map(([label, val]) => (
                    <span key={label}>{label}: <span style={{ color: "#B71C1C", fontWeight: "bold" }}>{val || 0}</span></span>
                ))}
                <span style={{ fontSize: "13px", fontWeight: "bold" }}>Total: <span style={{ color: "#1565C0", fontWeight: "bold" }}>{formData.TotalAmount || 0}</span></span>
            </div>

            {/* Remarks */}
            <div style={{ marginTop: "10px" }}>
                <label style={{ fontSize: "11px", fontWeight: "600" }}>Remarks</label>
                <textarea name="Remarks" value={formData.Remarks || ''} onChange={handleChange} rows={2}
                    style={{ width: "100%", border: "1px solid #ccc", borderRadius: "4px", fontSize: "12px", padding: "4px 6px" }} />
            </div>

            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>Delete Item?</DialogTitle>
                <DialogContent><DialogContentText>Remove this item from the order?</DialogContentText></DialogContent>
                <DialogActions>
                    <button onClick={closeConfirmDialog} style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleDelete} style={{ backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>Confirm</button>
                </DialogActions>
            </Dialog>

            {/* Item Rate & Qty Modal */}
            <Dialog open={itemModalOpen} onClose={() => setItemModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle style={{ fontSize: '14px', fontWeight: 700, paddingBottom: '4px' }}>
                    Add Item — {pendingItem?.label}
                </DialogTitle>
                <DialogContent style={{ paddingTop: '8px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Quantity</label>
                            <input type="number" value={itemModalQty} onChange={(e) => setItemModalQty(e.target.value)}
                                autoFocus min="0"
                                style={{ width: '100%', height: '32px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px', padding: '0 8px', textAlign: 'right' }} />
                        </div>
                        <div>
                            <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>Rate</label>
                            <input type="number" value={itemModalRate} onChange={(e) => setItemModalRate(e.target.value)}
                                min="0"
                                style={{ width: '100%', height: '32px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px', padding: '0 8px', textAlign: 'right' }} />
                        </div>
                        <div style={{ fontSize: '12px', color: '#555' }}>
                            GST Rate: <strong style={{ color: 'darkmagenta' }}>{pendingItem?.GSTRate || 0}%</strong>
                            &nbsp;&nbsp;Amount: <strong style={{ color: '#1565C0' }}>
                                {(parseFloat(itemModalQty || 0) * parseFloat(itemModalRate || 0)).toFixed(2)}
                            </strong>
                            &nbsp;&nbsp;Balance Stock: <strong style={{ color: '#2e7d32' }}>{pendingItem?.BalanceQuantity || 0}</strong>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setItemModalOpen(false)} style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleItemModalConfirm} style={{ backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}>Add</button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default PurchaseOrderEdit;
